import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Controller,
  FieldPath,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';
import { RefreshCwIcon, Trash2 } from 'lucide-react';

import { Button } from 'components/Button';
import { Checkbox } from 'components/Checkbox';
import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import FormInputWrapper from 'components/input/FormInputWrapper';
import { Input } from 'components/input/Input';
import { Flex, Grid } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { MGBAccessorialChargeSuggestion } from 'hooks/useFetchMGBAccessorialCharges';
import { useFetchModes } from 'hooks/useFetchModes';
import useTMSContext from 'hooks/useTMSContext';
import { CostLineItem, NormalizedLoad, TMSEquipmentType } from 'types/Load';
import { MaybeUndef } from 'types/UtilityTypes';
import { cn } from 'utils/shadcn';

import { LoadBuildingTextInput } from './LoadBuildingTextInput';
import {
  accessorialCode,
  accessorialsFromRateLineItems,
  buildMGBAccessorialSelectOptions,
  fromAccessorialOptionValue,
  shouldMirrorAccessorialsFromRates,
  toAccessorialOptionValue,
} from './accessorialOptions';
import {
  COLLECTION_METHOD_OPTIONS,
  MGBFscRateQualifier,
  MGB_DEFAULT_FSC_RATE_QUALIFIER,
  MGB_DEFAULT_RATE_MODE,
  MGB_FSC_RATE_QUALIFIER,
  MGB_FSC_RATE_QUALIFIER_OPTIONS,
  MGB_RATES_ACCESSORIALS_SECTION_ID,
} from './constants';
import { MGBLineHaulUnit } from './types';
import { useMGBFlashMileage } from './useMGBFlashMileage';
import {
  clearMGBFscFieldsForQualifier,
  computeMGBCustomerTotalCharge,
  computeMGBFscTotal,
  computeMGBLineHaulFromPerMile,
  deriveMGBFscRateQualifier,
  isMGBRatePerMileMode,
  mgbRouteStopsReadyForFlashMileage,
  normalizeMGBRateMode,
} from './utils';

const HAZMAT_LABEL = 'Hazardous Material';

type RatesFormProps = {
  serviceTypes?: MaybeUndef<TMSEquipmentType[]>;
  isServiceTypesLoading?: boolean;
  isAccessorialChargesLoading?: boolean;
  customerSelected?: boolean;
  onRefreshServiceTypes?: () => void;
  accessorialChargeSuggestions?: MGBAccessorialChargeSuggestion[];
};

export default function RatesForm({
  serviceTypes,
  isServiceTypesLoading = false,
  isAccessorialChargesLoading = false,
  customerSelected = true,
  onRefreshServiceTypes,
  accessorialChargeSuggestions = [],
}: RatesFormProps): React.JSX.Element {
  const { tmsID } = useTMSContext();
  const { modes, isLoading: isModesLoading } = useFetchModes(tmsID);
  const {
    control,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useFormContext<NormalizedLoad>();

  const watchedLineItems = useWatch({
    control,
    name: 'rateData.customerLineItems',
  });
  const lineHaul = useWatch({
    control,
    name: 'rateData.customerLineHaulCharge.val',
  });
  const lineHaulUnit = useWatch({
    control,
    name: 'rateData.customerLineHaulUnit',
  });
  const ratePerMile = useWatch({
    control,
    name: 'rateData.customerLineHaulRate',
  });
  const fscPercent = useWatch({ control, name: 'rateData.fscPercent' });
  const fscFlatRate = useWatch({ control, name: 'rateData.fscFlatRate' });
  const fscPerMile = useWatch({ control, name: 'rateData.fscPerMile' });
  const miles = useWatch({ control, name: 'rateData.miles' });
  const pickup = useWatch({ control, name: 'pickup' });
  const consignee = useWatch({ control, name: 'consignee' });
  const stops = useWatch({ control, name: 'stops' });

  const ratePerMileEnabled = isMGBRatePerMileMode(lineHaulUnit);

  const hasHazmatAmount = Boolean(getHazmatLineItem(watchedLineItems));
  const [showHazmatAmount, setShowHazmatAmount] = useState(hasHazmatAmount);
  const [fscQualifier, setFscQualifier] = useState<MGBFscRateQualifier>(() =>
    deriveMGBFscRateQualifier(getValues('rateData'))
  );

  const { isLoadingMiles, milesError } = useMGBFlashMileage({
    control,
    setValue,
    getValues,
    enabled: true,
  });

  const stopsReadyForMiles = mgbRouteStopsReadyForFlashMileage({
    stops,
    pickup,
    consignee,
  });

  const accessorialOptions = useMemo(
    () => buildMGBAccessorialSelectOptions(serviceTypes ?? []),
    [serviceTypes]
  );
  const hasAccessorialOptions = accessorialOptions.length > 0;
  const suggestedAmountByCode = useMemo(() => {
    const map = new Map<string, number>();
    for (const suggestion of accessorialChargeSuggestions) {
      const code = suggestion.accessorialCode?.trim().toUpperCase();
      if (!code || suggestion.suggestedAmount == null) {
        continue;
      }
      if (!Number.isFinite(suggestion.suggestedAmount)) {
        continue;
      }
      // First mapping wins when :P/:D both exist for the same code.
      if (!map.has(code)) {
        map.set(code, suggestion.suggestedAmount);
      }
    }
    return map;
  }, [accessorialChargeSuggestions]);
  const isAccessorialSelectLoading =
    isServiceTypesLoading || isAccessorialChargesLoading;
  const flatLineHaulBeforePmRef = useRef<number | null>(null);
  const ratesAccessorialsActivatedRef = useRef(false);

  useEffect(() => {
    if (hasHazmatAmount) {
      setShowHazmatAmount(true);
    }
  }, [hasHazmatAmount]);

  useEffect(() => {
    const subscription = watch((_values, { name }) => {
      if (name != null) {
        return;
      }
      ratesAccessorialsActivatedRef.current = false;
      setFscQualifier(deriveMGBFscRateQualifier(getValues('rateData')));
    });
    return () => subscription.unsubscribe();
  }, [watch, getValues]);

  // Auto line haul = miles × rate per mile when Rate Per Mile is on.
  // When rate/miles incomplete, clear any leftover FR total so it is not shown as computed.
  useEffect(() => {
    if (!ratePerMileEnabled) {
      return;
    }
    const total = computeMGBLineHaulFromPerMile(miles, ratePerMile);
    const current = Number(getValues('rateData.customerLineHaulCharge.val'));
    if (total == null) {
      if (Number.isFinite(current) && current > 0) {
        setValue('rateData.customerLineHaulCharge.val', undefined as never, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
      return;
    }
    if (Number.isFinite(current) && Math.abs(current - total) < 0.005) {
      return;
    }
    setValue('rateData.customerLineHaulCharge.val', total, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [ratePerMileEnabled, miles, ratePerMile, getValues, setValue]);

  const onRatePerMileToggle = (checked: boolean) => {
    if (checked) {
      const prevFlat = Number(getValues('rateData.customerLineHaulCharge.val'));
      flatLineHaulBeforePmRef.current =
        Number.isFinite(prevFlat) && prevFlat > 0 ? prevFlat : null;

      setValue('rateData.customerLineHaulUnit', MGBLineHaulUnit.PerMile, {
        shouldDirty: true,
      });
      setValue('rateData.customerLineHaulRate', null, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('rateData.customerLineHaulCharge.val', undefined as never, {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }

    setValue('rateData.customerLineHaulUnit', MGBLineHaulUnit.Flat, {
      shouldDirty: true,
    });

    const currentCharge = Number(
      getValues('rateData.customerLineHaulCharge.val')
    );
    let flatLineHaul: number | null = null;
    if (Number.isFinite(currentCharge) && currentCharge > 0) {
      flatLineHaul = currentCharge;
    } else if (flatLineHaulBeforePmRef.current != null) {
      flatLineHaul = flatLineHaulBeforePmRef.current;
      setValue('rateData.customerLineHaulCharge.val', flatLineHaul, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    flatLineHaulBeforePmRef.current = null;

    setValue('rateData.customerLineHaulRate', flatLineHaul, {
      shouldDirty: true,
    });
  };

  const removeHazmatAmount = () => {
    const items = watchedLineItems ?? [];
    setValue(
      'rateData.customerLineItems',
      items.filter((item) => item.label !== HAZMAT_LABEL),
      { shouldDirty: true }
    );
    setShowHazmatAmount(false);
  };

  const accessorialCharges = (watchedLineItems ?? []).filter(
    (item) => item.label !== HAZMAT_LABEL
  );

  // Specs accessorials are read-only — mirror Rates when Rates has coded
  // selections. Do not wipe Specs-only hydrate (edit/suggestion) before the
  // user picks accessorials in Rates.
  const watchedCustomerExternalTMSID = useWatch({
    control,
    name: 'customer.externalTMSID',
  });
  const prevCustomerIdForAccessorialsRef = useRef(
    watchedCustomerExternalTMSID?.trim() ?? ''
  );
  useEffect(() => {
    const nextId = watchedCustomerExternalTMSID?.trim() ?? '';
    const prevId = prevCustomerIdForAccessorialsRef.current;
    if (prevId === nextId) {
      return;
    }
    prevCustomerIdForAccessorialsRef.current = nextId;

    if (!prevId && nextId) {
      return;
    }

    ratesAccessorialsActivatedRef.current = false;
  }, [watchedCustomerExternalTMSID]);

  useEffect(() => {
    const next = accessorialsFromRateLineItems(watchedLineItems, HAZMAT_LABEL);
    if (next.length > 0) {
      ratesAccessorialsActivatedRef.current = true;
    }
    if (
      !shouldMirrorAccessorialsFromRates(
        next,
        ratesAccessorialsActivatedRef.current
      )
    ) {
      return;
    }

    const current = getValues('accessorials') ?? [];
    const same =
      current.length === next.length &&
      current.every((item, index) => {
        const other = next[index];
        return (
          other != null &&
          accessorialCode(item) === accessorialCode(other) &&
          (item.displayText ?? '') === (other.displayText ?? '')
        );
      });
    if (same) {
      return;
    }
    // Rate line edits already dirty the form; mirroring must not.
    setValue('accessorials', next, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [watchedLineItems, getValues, setValue]);

  const onFscQualifierChange = (next: MGBFscRateQualifier) => {
    setFscQualifier(next);
    clearMGBFscFieldsForQualifier(
      setValue as (
        name: string,
        value: number | null,
        options?: { shouldDirty?: boolean }
      ) => void,
      next
    );
  };

  // Mode dropdown: exclude "Flat" (rate type). Keep Flatbed (ENG-7739).
  const modeOptions = useMemo(
    () =>
      (modes ?? []).filter(
        (mode) => mode.name?.trim().toLowerCase() !== 'flat'
      ),
    [modes]
  );

  const fscTotal = computeMGBFscTotal({
    qualifier: fscQualifier,
    lineHaul,
    fscPercent,
    fscFlatRate,
    fscPerMile,
    miles,
  });

  const customerTotalCharge = computeMGBCustomerTotalCharge({
    customerLineHaulCharge: { val: lineHaul },
    fscPercent,
    fscFlatRate,
    fscPerMile,
    miles,
    customerLineItems: watchedLineItems,
  });

  const fscRateFieldName = mgbFscRateFieldName(fscQualifier);
  const fscRateUnit = mgbFscRateUnit(fscQualifier);
  const fscRateOptions =
    fscQualifier === MGB_FSC_RATE_QUALIFIER.Percent
      ? {
          max: { value: 100, message: 'FSC % must be between 0 and 100' },
        }
      : undefined;

  const milesPlaceholder = (() => {
    if (!stopsReadyForMiles) {
      return 'Enter at least two stops to calculate distance';
    }
    if (isLoadingMiles) {
      return 'Calculating miles…';
    }
    if (milesError) {
      return milesError;
    }
    return '—';
  })();

  return (
    <Grid cols='2' gap='lg' className='mx-0 w-full'>
      <Typography
        variant='body-sm'
        weight='semibold'
        className='col-span-2 mb-2 text-neutral-700'
      >
        Customer Rates
      </Typography>

      <div className='col-span-2'>
        <Label name='rateData.collectionMethod' required>
          Payment Method
        </Label>
        <Controller
          name='rateData.collectionMethod'
          control={control}
          rules={{ required: 'Payment method is required' }}
          render={({ field }) => (
            <Select
              value={field.value || undefined}
              onValueChange={field.onChange}
            >
              <SelectTrigger className='mt-1 text-[13px] border-neutral-input-border'>
                <SelectValue placeholder='Select method' />
              </SelectTrigger>
              <SelectContent>
                {COLLECTION_METHOD_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        <ErrorMessage
          errors={errors}
          name='rateData.collectionMethod'
          render={({ message }: { message: string }) => (
            <Typography variant='body-xs' className='text-error-500 mt-1'>
              {message}
            </Typography>
          )}
        />
      </div>

      <div className='col-span-2'>
        <Label name='rateData.customerRateType' required={false}>
          Mode
        </Label>
        <Controller
          name='rateData.customerRateType'
          control={control}
          defaultValue={MGB_DEFAULT_RATE_MODE}
          render={({ field }) => {
            const selected = normalizeMGBRateMode(field.value);
            const hasSelected = modeOptions.some(
              (mode) => mode.name === selected
            );
            return (
              <Select
                value={selected}
                onValueChange={(value) => {
                  field.onChange(normalizeMGBRateMode(value));
                }}
                disabled={isModesLoading}
              >
                <SelectTrigger className='mt-1 text-[13px] border-neutral-input-border'>
                  <SelectValue
                    placeholder={
                      isModesLoading ? 'Loading modes...' : 'Select mode'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {!hasSelected && selected ? (
                    <SelectItem value={selected}>{selected}</SelectItem>
                  ) : null}
                  {modeOptions.map((mode) => (
                    <SelectItem key={mode.id} value={mode.name}>
                      {mode.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          }}
        />
      </div>

      <div className='col-span-2'>
        <Checkbox
          id='mgb-rate-per-mile'
          label='Rate Per Mile'
          checked={ratePerMileEnabled}
          onCheckedChange={(v) => onRatePerMileToggle(v === true)}
          labelClassName='text-[13px] text-neutral-800'
        />
      </div>

      {ratePerMileEnabled ? (
        <>
          <div className='col-span-1'>
            <Label name='rateData.miles' required={false}>
              Total Miles
            </Label>
            <Input
              type='number'
              step={0.01}
              className='mt-1'
              value={
                miles != null && Number.isFinite(Number(miles))
                  ? String(miles)
                  : ''
              }
              placeholder={milesPlaceholder}
              disabled
              readOnly
            />
            {isLoadingMiles ? (
              <Typography variant='body-xs' className='text-neutral-500 mt-1'>
                Looking up miles from Kenco Flash…
              </Typography>
            ) : null}
            {milesError && !isLoadingMiles ? (
              <Typography variant='body-xs' className='text-error-500 mt-1'>
                {milesError}
              </Typography>
            ) : null}
            <ErrorMessage
              errors={errors}
              name='rateData.miles'
              render={({ message }: { message: string }) => (
                <Typography variant='body-xs' className='text-error-500 mt-1'>
                  {message}
                </Typography>
              )}
            />
          </div>
          <div className='col-span-1'>
            <LoadBuildingTextInput
              name='rateData.customerLineHaulRate'
              label='Rate Per Mile'
              inputType='number'
              step={0.01}
              inlineDimensionUnit='USD'
            />
          </div>
        </>
      ) : null}

      <div className='col-span-2'>
        <LoadBuildingTextInput
          name='rateData.customerLineHaulCharge.val'
          label='Line Haul Rate'
          inputType='number'
          step={0.01}
          inlineDimensionUnit='USD'
          readOnly={ratePerMileEnabled}
        />
        {ratePerMileEnabled ? (
          <Typography variant='body-xs' className='text-neutral-500 mt-1'>
            Auto-calculated from total miles × rate per mile
          </Typography>
        ) : null}
      </div>

      <div className='col-span-1'>
        <LoadBuildingTextInput
          key={fscRateFieldName}
          name={fscRateFieldName}
          label='FSC Rate'
          inputType='number'
          step={0.01}
          inlineDimensionUnit={fscRateUnit}
          options={fscRateOptions}
        />
      </div>
      <div className='col-span-1'>
        <Label name='rateData.fscRateQualifier' required={false}>
          FSC Qualifier
        </Label>
        <Select
          value={fscQualifier || MGB_DEFAULT_FSC_RATE_QUALIFIER}
          onValueChange={(value) =>
            onFscQualifierChange(value as MGBFscRateQualifier)
          }
        >
          <SelectTrigger className='mt-1 h-8 text-[13px] border-neutral-input-border'>
            <SelectValue placeholder='Select qualifier' />
          </SelectTrigger>
          <SelectContent>
            {MGB_FSC_RATE_QUALIFIER_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {fscQualifier === MGB_FSC_RATE_QUALIFIER.PerMile ? (
        <div className='col-span-2'>
          <Label name='rateData.miles' required={false}>
            Distance (miles)
          </Label>
          <Input
            type='number'
            step={0.01}
            className='mt-1'
            value={
              miles != null && Number.isFinite(Number(miles))
                ? String(miles)
                : ''
            }
            placeholder={milesPlaceholder}
            disabled
            readOnly
          />
          <Typography variant='body-xs' className='text-neutral-500 mt-1'>
            Same lane miles used for Rate Per Mile (from Kenco Flash when stops
            are set)
          </Typography>
          <ErrorMessage
            errors={errors}
            name='rateData.miles'
            render={({ message }: { message: string }) => (
              <Typography variant='body-xs' className='text-error-500 mt-1'>
                {message}
              </Typography>
            )}
          />
        </div>
      ) : null}

      {fscTotal != null ? (
        <Flex align='center' className='col-span-2'>
          <Typography variant='body-sm' className='text-neutral-600'>
            Fuel Surcharge Total:{' '}
            <Typography as='span' variant='body-sm' weight='semibold'>
              ${fscTotal.toFixed(2)} USD
            </Typography>
          </Typography>
        </Flex>
      ) : null}

      <div className='col-span-2' id={MGB_RATES_ACCESSORIALS_SECTION_ID}>
        <Flex direction='col' gap='sm' className='w-full'>
          <Flex align='center' justify='between' className='w-full'>
            <Flex align='center' gap='xs'>
              <Typography variant='body-sm' weight='semibold'>
                Accessorials
              </Typography>
              {onRefreshServiceTypes &&
              customerSelected &&
              accessorialCharges.length > 0 ? (
                <button
                  type='button'
                  title='Refresh accessorials'
                  aria-label='Refresh accessorials'
                  onClick={onRefreshServiceTypes}
                  disabled={isAccessorialSelectLoading}
                  className={cn(
                    'shrink-0 p-1 hover:text-info-600 rounded-full transition-colors',
                    isAccessorialSelectLoading
                      ? 'cursor-not-allowed opacity-50'
                      : 'cursor-pointer'
                  )}
                >
                  {isAccessorialSelectLoading ? (
                    <ButtonLoader className='mr-0' />
                  ) : (
                    <RefreshCwIcon className='h-4 w-4' />
                  )}
                </button>
              ) : null}
            </Flex>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-8 px-3 text-xs text-brand-500'
              buttonNamePosthog={null}
              onClick={() => {
                const items = getValues('rateData.customerLineItems') ?? [];
                setValue(
                  'rateData.customerLineItems',
                  [
                    ...items,
                    {
                      label: '',
                      accessorialCode: '',
                      unitBasis: 'flat',
                      quantity: 1,
                      ratePerUnitUSD: 0,
                      totalChargeUSD: 0,
                      note: '',
                    } as CostLineItem,
                  ],
                  { shouldDirty: true }
                );
              }}
            >
              + Add Accessorial
            </Button>
          </Flex>
          {accessorialCharges.map((item, idx) => {
            const fullIndex = (watchedLineItems ?? []).findIndex(
              (li) => li === item
            );
            const index =
              fullIndex >= 0
                ? fullIndex
                : // Fallback: match by position among non-hazmat
                  ((watchedLineItems ?? [])
                    .map((li, i) => ({ li, i }))
                    .filter(({ li }) => li.label !== HAZMAT_LABEL)[idx]?.i ??
                  idx);
            return (
              <AccessorialChargeRow
                key={`acc-${index}-${item.accessorialCode ?? item.label ?? ''}`}
                index={index}
                accessorialOptions={accessorialOptions}
                hasAccessorialOptions={hasAccessorialOptions}
                isAccessorialSelectLoading={isAccessorialSelectLoading}
                customerSelected={customerSelected}
                suggestedAmountByCode={suggestedAmountByCode}
                selectedCodes={accessorialCharges
                  .map((c) => c.accessorialCode?.trim() ?? '')
                  .filter(Boolean)}
              />
            );
          })}
        </Flex>
      </div>

      <div className='col-span-2'>
        {!showHazmatAmount ? (
          <Flex align='center' justify='end' className='col-span-4 mr-px'>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-8 px-3 text-xs text-brand-500 hover:-mx-px'
              buttonNamePosthog={null}
              onClick={() => setShowHazmatAmount(true)}
            >
              Add Hazmat Amount
            </Button>
          </Flex>
        ) : (
          <Flex direction='col' gap='xs'>
            <FormInputWrapper
              name='rateData.customerLineItems'
              label='Hazardous Material Amount'
              required={false}
            >
              <HazardousMaterialAmountInput />
            </FormInputWrapper>
            <Flex align='center' justify='end' className='w-full mr-px'>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='h-8 px-3 text-xs text-brand-500 hover:-mx-px'
                buttonNamePosthog={null}
                onClick={removeHazmatAmount}
              >
                Remove Hazmat Amount
              </Button>
            </Flex>
          </Flex>
        )}
      </div>

      <Flex
        align='center'
        justify='between'
        className='col-span-2 mt-1 border-t border-neutral-200 pt-3'
      >
        <Typography
          variant='body-sm'
          weight='semibold'
          className='text-neutral-700'
        >
          Total Customer Rate
        </Typography>
        <Typography
          variant='body-sm'
          weight='semibold'
          className='text-neutral-900'
          aria-label='Total Customer Rate'
        >
          ${customerTotalCharge.toFixed(2)} USD
        </Typography>
      </Flex>
    </Grid>
  );
}

function AccessorialChargeRow({
  index,
  accessorialOptions,
  hasAccessorialOptions,
  isAccessorialSelectLoading,
  customerSelected,
  selectedCodes,
  suggestedAmountByCode,
}: {
  index: number;
  accessorialOptions: { label: string; value: string }[];
  hasAccessorialOptions: boolean;
  isAccessorialSelectLoading: boolean;
  customerSelected: boolean;
  selectedCodes: string[];
  suggestedAmountByCode: Map<string, number>;
}) {
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<NormalizedLoad>();
  const label = useWatch({
    control,
    name: `rateData.customerLineItems.${index}.label`,
  });
  const accessorialCode = useWatch({
    control,
    name: `rateData.customerLineItems.${index}.accessorialCode`,
  });

  const selectValue = accessorialCode
    ? toAccessorialOptionValue(String(accessorialCode), String(label ?? ''))
    : undefined;

  // Ensure hydrated load values without a matching service-types option still display.
  const options = useMemo(() => {
    if (!selectValue) {
      return accessorialOptions;
    }
    if (accessorialOptions.some((o) => o.value === selectValue)) {
      return accessorialOptions;
    }
    const { code, name } = fromAccessorialOptionValue(selectValue);
    const display =
      code && name && name.toLowerCase() !== code.toLowerCase()
        ? `${name} (${code})`
        : name || code;
    return [...accessorialOptions, { label: display, value: selectValue }];
  }, [accessorialOptions, selectValue]);

  let accessorialPlaceholder = 'Select accessorial';
  if (!customerSelected) {
    accessorialPlaceholder = 'Select a customer first';
  } else if (isAccessorialSelectLoading) {
    accessorialPlaceholder = 'Loading accessorials…';
  } else if (!hasAccessorialOptions) {
    accessorialPlaceholder = 'No accessorials found';
  }

  return (
    <Flex gap='sm' align='end' className='w-full'>
      <div className='min-w-0 flex-1'>
        <Label name={`rateData.customerLineItems.${index}.label`}>
          Service
        </Label>
        <Select
          value={selectValue}
          onValueChange={(value) => {
            const { code, name } = fromAccessorialOptionValue(value);
            setValue(
              `rateData.customerLineItems.${index}.label`,
              name || code,
              {
                shouldDirty: true,
                shouldValidate: true,
              }
            );
            setValue(
              `rateData.customerLineItems.${index}.accessorialCode`,
              code,
              { shouldDirty: true }
            );
            setValue(`rateData.customerLineItems.${index}.unitBasis`, 'flat', {
              shouldDirty: true,
            });
            setValue(`rateData.customerLineItems.${index}.quantity`, 1, {
              shouldDirty: true,
            });
            const suggested = suggestedAmountByCode.get(
              code.trim().toUpperCase()
            );
            const amount =
              suggested != null && Number.isFinite(suggested) ? suggested : 0;
            setValue(
              `rateData.customerLineItems.${index}.totalChargeUSD`,
              amount,
              { shouldDirty: true, shouldValidate: true }
            );
            setValue(
              `rateData.customerLineItems.${index}.ratePerUnitUSD`,
              amount,
              { shouldDirty: true }
            );
          }}
          disabled={
            !customerSelected ||
            isAccessorialSelectLoading ||
            !hasAccessorialOptions
          }
        >
          <SelectTrigger className='mt-1 text-[13px] border-neutral-input-border'>
            <SelectValue placeholder={accessorialPlaceholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => {
              const code = fromAccessorialOptionValue(option.value).code;
              const takenByOther =
                Boolean(code) &&
                selectedCodes.includes(code) &&
                code !== String(accessorialCode ?? '');
              return (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  disabled={takenByOther}
                >
                  {option.label}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <ErrorMessage
          errors={errors}
          name={`rateData.customerLineItems.${index}.label`}
          render={({ message }: { message: string }) => (
            <Typography variant='body-xs' className='text-error-500 mt-1'>
              {message}
            </Typography>
          )}
        />
      </div>
      <div className='w-28 shrink-0'>
        <Label name={`rateData.customerLineItems.${index}.totalChargeUSD`}>
          Amount
        </Label>
        <Controller
          name={`rateData.customerLineItems.${index}.totalChargeUSD`}
          control={control}
          render={({ field }) => (
            <Input
              type='number'
              step={0.01}
              className='mt-1'
              placeholder='0.00'
              value={
                field.value === 0 || field.value == null
                  ? ''
                  : String(field.value)
              }
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  field.onChange(0);
                  setValue(
                    `rateData.customerLineItems.${index}.ratePerUnitUSD`,
                    0,
                    { shouldDirty: true }
                  );
                  return;
                }
                const n = Number(raw);
                if (Number.isNaN(n) || n < 0) {
                  return;
                }
                field.onChange(n);
                setValue(
                  `rateData.customerLineItems.${index}.ratePerUnitUSD`,
                  n,
                  { shouldDirty: true }
                );
                setValue(`rateData.customerLineItems.${index}.quantity`, 1, {
                  shouldDirty: true,
                });
                setValue(
                  `rateData.customerLineItems.${index}.unitBasis`,
                  'flat',
                  { shouldDirty: true }
                );
              }}
            />
          )}
        />
      </div>
      <Button
        type='button'
        variant='ghost'
        size='sm'
        className='h-9 shrink-0 px-2 text-neutral-500'
        buttonNamePosthog={null}
        title='Remove accessorial'
        onClick={() => {
          const items = getValues('rateData.customerLineItems') ?? [];
          setValue(
            'rateData.customerLineItems',
            items.filter((_, i) => i !== index),
            { shouldDirty: true }
          );
        }}
      >
        <Trash2 className='h-4 w-4' />
      </Button>
    </Flex>
  );
}

function mgbFscRateFieldName(
  qualifier: MGBFscRateQualifier
): FieldPath<NormalizedLoad> {
  if (qualifier === MGB_FSC_RATE_QUALIFIER.Percent) {
    return 'rateData.fscPercent';
  }
  if (qualifier === MGB_FSC_RATE_QUALIFIER.FlatRate) {
    return 'rateData.fscFlatRate';
  }
  return 'rateData.fscPerMile';
}

function mgbFscRateUnit(qualifier: MGBFscRateQualifier): string {
  if (qualifier === MGB_FSC_RATE_QUALIFIER.Percent) {
    return '%';
  }
  if (qualifier === MGB_FSC_RATE_QUALIFIER.FlatRate) {
    return 'USD';
  }
  return 'USD/mi';
}

function getHazmatLineItem(
  items: CostLineItem[] | undefined
): CostLineItem | undefined {
  return items?.find((item) => item.label === HAZMAT_LABEL);
}

function HazardousMaterialAmountInput() {
  const { control } = useFormContext<NormalizedLoad>();

  return (
    <Controller
      name='rateData.customerLineItems'
      control={control}
      render={({ field }) => {
        const items = field.value ?? [];
        const hazmat = getHazmatLineItem(items);
        const amount = hazmat?.totalChargeUSD ?? '';

        return (
          <Input
            type='number'
            step={0.01}
            className='mt-1'
            placeholder='0.00'
            value={amount === '' ? '' : String(amount)}
            onChange={(e) => {
              const raw = e.target.value;
              const rest = items.filter((item) => item.label !== HAZMAT_LABEL);
              if (raw === '') {
                field.onChange(rest);
                return;
              }
              const n = Number(raw);
              if (Number.isNaN(n) || n <= 0) {
                field.onChange(rest);
                return;
              }
              const lineItem: CostLineItem = {
                label: HAZMAT_LABEL,
                unitBasis: 'flat',
                quantity: 1,
                ratePerUnitUSD: n,
                totalChargeUSD: n,
                note: '',
              };
              field.onChange([...rest, lineItem]);
            }}
          />
        );
      }}
    />
  );
}
